import { putAccount } from "@backend/AccountRequests";
import { getAllSchoolsSimpleRequest } from "@backend/SchoolsRequests";
import { deleteUserCall } from "@backend/UsersRequests";
import { hasValue } from "@helpers/validations/common";

/**
 * ViewModel for managing user administration functionalities in the Users Administration Page.
 *
 * @function UsersAdministrationPageViewModel
 * @param {Object} _view - The view object containing references to UI components and methods.
 * @param {Object} _view.tableTemplateRef - A reference to the table template component.
 * @param {Object} _view.tableTemplateRef.current - The current instance of the table template component.
 * @param {Function} _view.tableTemplateRef.current.getItems - A method to refresh or retrieve items in the table.
 * @param {Function} _view.setSchoolList - A method to set the list of schools in the view.
 * @returns {Object} An object containing methods for user administration actions.
 * @property {Function} tableTemplateGetItems - Refreshes the table items by invoking the `getItems` method on the table template reference.
 * @property {Function} enableUser - Enables a user account and refreshes the table items upon success.
 * @property {Function} deleteUser - Deletes a user by their ID and refreshes the table items upon success.
 * @property {Function} blockUser - Blocks a user by updating their account status and refreshes the table items upon success.
 * @property {Function} getSchoolList - Retrieves the list of schools and sets the school list in the view.
 */
function UsersAdministrationPageViewModel(_view) {
    function tableTemplateGetItems() {
        _view.tableTemplateRef.current.getItems();
    }

    async function enableUser(row) {
        console.log(row);
        const data = await putAccount({ ...row, isEnabled: true, isUserAllowed: true });
        if (data.success) {
            _view.tableTemplateRef.current.getItems();
        }
    }

    /**
     * Deletes a user by their ID and refreshes the table items if the operation is successful.
     *
     * @async
     * @function deleteUser
     * @param {string|number} id - The unique identifier of the user to be deleted.
     * @returns {Promise<void>} Resolves when the user deletion process is complete.
     */
    async function deleteUser(id) {
        const data = await deleteUserCall(id);
        if (data.success) {
            _view.tableTemplateRef.current.getItems();
        }
    }

    /**
     * Blocks a user by updating their account status to disallow access.
     *
     * @async
     * @function blockUser
     * @param {Object} row - The user data object representing the row to be updated.
     * @param {string|number} row.id - The unique identifier of the user to be blocked.
     * @param {string} row.email - The email address of the user to be blocked.
     * @param {boolean} row.isUserAllowed - Indicates whether the user is allowed access (set to `false` to block the user).
     * @returns {Promise<void>} Resolves when the user is successfully blocked and the table is refreshed.
     */
    async function blockUser(row) {
        const data = await putAccount({ ...row, isEnabled: false, isUserAllowed: false });
        if (data.success) {
            _view.tableTemplateRef.current.getItems();
        }
    }

    /**
     * Fetches the list of schools and updates the view with the retrieved data.
     * 
     * This function makes an asynchronous request to fetch a simplified list of all schools.
     * If the request is successful, it updates the view model with the retrieved school list.
     * 
     * @async
     * @function getSchoolList
     * @returns {Promise<void>} A promise that resolves when the school list is fetched and the view is updated.
     */
    async function getSchoolList() {
        const resp = await getAllSchoolsSimpleRequest();

        if (resp.success) {
            _view.setSchoolList(resp.data);
        }
    }


    return {
        tableTemplateGetItems,
        enableUser,
        deleteUser,
        blockUser,
        getSchoolList,
    };
}

export default UsersAdministrationPageViewModel;